import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  DocumentTextIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { useAuth } from "@/auth/AuthProvider";
import { validateShareToken, acceptShareInvite, revokeShareAccess, getScopeLabel } from "@/lib/share";
import { logEvent } from "@/lib/analytics";
import type { ShareTokenValidationResult } from "@/types/share";

type ViewerState =
  | { stage: "validating" }
  | { stage: "invalid"; reason: string }
  | { stage: "anonymous";     validation: ShareTokenValidationResult }
  | { stage: "checking_membership"; validation: ShareTokenValidationResult }
  | { stage: "already_member";  validation: ShareTokenValidationResult; redirectPath: string }
  | { stage: "ready_to_accept"; validation: ShareTokenValidationResult }
  | { stage: "accepting" }
  | { stage: "accepted";  redirectPath: string }
  | { stage: "revoking" }
  | { stage: "revoked" };

/**
 * Public share viewer — accessible without authentication.
 *
 * Flow:
 *  1. Validate token against Firestore (unauthenticated read allowed).
 *  2a. Not signed in  → prompt to sign in / create account.
 *  2b. Signed in, not yet a member → "Accept Viewer Access" button.
 *  2c. Signed in, already a member → "Go to Case" + "Remove My Access".
 */
export default function ShareViewerPage() {
  const { linkId }      = useParams<{ linkId: string }>();
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const token           = searchParams.get("token") ?? "";
  const { user, loading: authLoading } = useAuth();

  const [state, setState] = useState<ViewerState>({ stage: "validating" });

  // ── Step 1: Validate token ──────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return; // wait for auth to settle before acting

    const validate = async () => {
      if (!linkId || !token) {
        setState({
          stage: "invalid",
          reason: !token
            ? "This share link is incomplete — the access token is missing. Please ask the case owner to copy the link again from the Share page and send it to you."
            : "Invalid share link URL.",
        });
        return;
      }

      try {
        const result = await validateShareToken(token, linkId);
        await logEvent("share_viewer_access", {
          linkId,
          valid: result.valid,
          reason: result.reason ?? "success",
        });

        if (!result.valid) {
          setState({ stage: "invalid", reason: result.reason ?? "Invalid or expired share link." });
          return;
        }

        if (!user) {
          // No account — show sign-in prompt
          setState({ stage: "anonymous", validation: result });
          return;
        }

        // ── Step 2: Check Firestore membership ─────────────────────────
        if (!result.caseId) {
          setState({ stage: "invalid", reason: "Share link is missing case reference." });
          return;
        }

        setState({ stage: "checking_membership", validation: result });

        const caseId    = result.caseId;
        const memberRef = doc(db, "cases", caseId, "members", user.uid);
        const memberSnap = await getDoc(memberRef);

        if (memberSnap.exists()) {
          const memberData = memberSnap.data();
          // Member exists — determine first scope redirect
          const scopes: string[] = memberData.permissions
            ? Object.keys(memberData.permissions).filter(
                k => k.startsWith("canRead") && (memberData.permissions as Record<string,boolean>)[k]
              ).map(k => k.replace("canRead", "").toLowerCase())
            : [];
          const firstScope = scopes[0] ?? "journal";
          setState({
            stage: "already_member",
            validation: result,
            redirectPath: `/cases/${caseId}/${firstScope}`,
          });
        } else {
          setState({ stage: "ready_to_accept", validation: result });
        }
      } catch (err) {
        console.error("[ShareViewerPage] Validation error:", err);
        setState({
          stage: "invalid",
          reason: err instanceof Error ? err.message : "Failed to validate share link.",
        });
      }
    };

    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.uid]);

  // ── Accept handler ────────────────────────────────────────────────────
  const handleAccept = async () => {
    if (!linkId) return;
    setState({ stage: "accepting" });
    try {
      const result = await acceptShareInvite(linkId, token);
      await logEvent("share_invite_accepted", { linkId, caseId: result.caseId });
      setState({ stage: "accepted", redirectPath: result.redirectPath });
    } catch (err) {
      console.error("[ShareViewerPage] Accept error:", err);
      const reason = err instanceof Error ? err.message : "Failed to accept invite.";
      setState({ stage: "invalid", reason });
    }
  };

  // ── Revoke handler ────────────────────────────────────────────────────
  const handleRevoke = async () => {
    if (!linkId) return;
    setState({ stage: "revoking" });
    try {
      await revokeShareAccess(linkId, token);
      await logEvent("share_access_revoked", { linkId });
      setState({ stage: "revoked" });
    } catch (err) {
      console.error("[ShareViewerPage] Revoke error:", err);
      const reason = err instanceof Error ? err.message : "Failed to revoke access.";
      setState({ stage: "invalid", reason });
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────

  if (state.stage === "validating" || state.stage === "checking_membership" || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-orange-500" />
          <p className="mt-4 text-sm text-slate-400">
            {authLoading || state.stage === "checking_membership"
              ? "Checking your access…"
              : "Validating share link…"}
          </p>
        </div>
      </div>
    );
  }

  if (state.stage === "accepting" || state.stage === "revoking") {
    const label = state.stage === "accepting" ? "Granting viewer access…" : "Removing your access…";
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-orange-500" />
          <p className="mt-4 text-sm text-slate-400">{label}</p>
        </div>
      </div>
    );
  }

  if (state.stage === "invalid") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md rounded-xl border border-red-500/40 bg-red-500/10 p-8 text-center">
          <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h1 className="mt-4 text-xl font-semibold text-white">Access Denied</h1>
          <p className="mt-2 text-sm text-slate-300">{state.reason}</p>
          <button
            onClick={() => navigate("/login")}
            className="mt-6 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-orange-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (state.stage === "accepted") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md rounded-xl border border-green-500/40 bg-green-500/10 p-8 text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
          <h1 className="mt-4 text-xl font-semibold text-white">Access Granted</h1>
          <p className="mt-2 text-sm text-slate-300">
            You now have read-only viewer access to this case.
          </p>
          <button
            onClick={() => navigate(state.redirectPath)}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-orange-700"
          >
            Go to Case <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (state.stage === "revoked") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md rounded-xl border border-slate-600 bg-slate-800 p-8 text-center">
          <XMarkIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h1 className="mt-4 text-xl font-semibold text-white">Access Removed</h1>
          <p className="mt-2 text-sm text-slate-300">
            Your viewer access has been revoked and this share link has been disabled.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-6 rounded-lg border border-slate-600 bg-slate-700 px-5 py-2.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // ── Shared content card (used for anonymous / ready_to_accept / already_member) ─
  const validation  = state.validation;
  const { caseId, scope, expiresAt } = validation;
  const scopes: string[] = (validation as { scopes?: string[] }).scopes ?? [];

  const loginRedirect = encodeURIComponent(
    `${window.location.pathname}${window.location.search}`
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur">
        <div className="mx-auto max-w-3xl px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="h-6 w-6 text-orange-400" />
            <div>
              <h1 className="text-sm font-semibold text-slate-50">ThreadLock — Shared Case View</h1>
              <p className="text-xs text-slate-400">Read-only evaluator access</p>
            </div>
          </div>
          <span className="rounded-full bg-blue-500/20 px-3 py-1 text-xs font-medium text-blue-300 capitalize">
            {scope} Access
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-6">

        {/* Access details card */}
        <div className="rounded-xl border border-slate-700 bg-slate-800 p-5">
          <h2 className="text-sm font-semibold text-white mb-3">Access Details</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-400">Case ID</dt>
              <dd className="font-mono text-slate-200">{caseId}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-400">Access level</dt>
              <dd className="text-slate-200 capitalize">{scope} — read-only</dd>
            </div>
            {scopes.length > 0 && (
              <div className="flex justify-between items-start">
                <dt className="text-slate-400">Sections shared</dt>
                <dd className="flex flex-wrap gap-1 justify-end">
                  {scopes.map(s => (
                    <span key={s} className="rounded-full bg-orange-500/20 px-2 py-0.5 text-xs text-orange-300">
                      {getScopeLabel(s as import("@/types/share").ShareScope)}
                    </span>
                  ))}
                </dd>
              </div>
            )}
            {expiresAt && (
              <div className="flex justify-between">
                <dt className="text-slate-400">Expires</dt>
                <dd className="text-slate-200">{new Date(expiresAt).toLocaleDateString()}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Watermark notice for evaluator scope */}
        {scope === "evaluator" && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
            <p className="text-sm font-medium text-amber-300">Watermark Notice</p>
            <p className="mt-1 text-xs text-amber-300/80">
              All downloaded documents will be watermarked for traceability.
            </p>
          </div>
        )}

        {/* ── Anonymous: prompt to sign in ── */}
        {state.stage === "anonymous" && (
          <div className="rounded-xl border border-slate-600 bg-slate-800 p-6 text-center space-y-4">
            <h3 className="text-base font-semibold text-white">
              Sign in to Accept Viewer Access
            </h3>
            <p className="text-sm text-slate-400">
              You need a ThreadLock account to access this case. Your viewer permissions
              will be granted automatically when you sign in.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/login?redirect=${loginRedirect}`}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-orange-700"
              >
                Sign In <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <Link
                to={`/signup?redirect=${loginRedirect}`}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 bg-slate-700 px-5 py-2.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
              >
                Create Account
              </Link>
            </div>
            <p className="text-xs text-slate-500">
              Free to sign up · No credit card required
            </p>
          </div>
        )}

        {/* ── Authenticated but not yet a member: accept button ── */}
        {state.stage === "ready_to_accept" && (
          <div className="rounded-xl border border-slate-600 bg-slate-800 p-6 text-center space-y-4">
            <h3 className="text-base font-semibold text-white">
              Accept Read-Only Access
            </h3>
            <p className="text-sm text-slate-400">
              You are signed in as <span className="text-slate-200">{user?.email}</span>.
              Click below to accept viewer access to this case. You can remove your
              access at any time.
            </p>
            <button
              onClick={handleAccept}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              Accept Viewer Access <ArrowRightIcon className="h-4 w-4" />
            </button>
            <p className="text-xs text-slate-500">
              Accepting grants read-only access. You cannot edit or delete any case content.
            </p>
          </div>
        )}

        {/* ── Already a member: go to case or revoke ── */}
        {state.stage === "already_member" && (
          <div className="rounded-xl border border-slate-600 bg-slate-800 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-400 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-white">You have viewer access to this case</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Signed in as {user?.email}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate(state.redirectPath)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-orange-700"
              >
                Go to Case <ArrowRightIcon className="h-4 w-4" />
              </button>
              <button
                onClick={handleRevoke}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-5 py-2.5 text-sm font-medium text-red-300 hover:bg-red-500/20"
              >
                <XMarkIcon className="h-4 w-4" />
                Remove My Access
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Removing your access will also disable this share link permanently.
            </p>
          </div>
        )}
      </main>

      <footer className="mt-12 border-t border-white/10 py-6">
        <div className="mx-auto max-w-3xl px-4 text-center text-xs text-slate-500">
          This is a read-only shared view generated by ThreadLock.
          To request different access, contact the case owner.
        </div>
      </footer>
    </div>
  );
}


